import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Phone, Mail, MapPin, Clock, Shield, Award, Thermometer, 
  Wind, Wrench, MessageCircle, Send, X, ChevronDown, 
  Star, CheckCircle, ArrowRight, Menu, Building2, Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo components using the provided assets
const LogoIcon = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png" 
    alt="Ayoka Énergie" 
    className="h-12 w-auto"
  />
);

const LogoFull = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/tlaizxyd_Logo_horizontal_blanc_avec_texte.png" 
    alt="Ayoka Énergie" 
    className="h-[88px] w-auto"
  />
);

// Navigation Component
const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "À propos" },
    { href: "#realisations", label: "Réalisations" },
    { href: "#devis", label: "Devis" },
    { href: "#contact", label: "Contact" },
    { href: "/partenaire", label: "Partenaire" },
  ];

  return (
    <>
      {/* Top Bar with contact info */}
      <div className={`fixed top-0 left-0 right-0 z-50 bg-black text-white transition-all duration-500 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'py-2'}`}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="tel:0788484052" className="flex items-center gap-2 hover:text-[#EEC17E] transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span>07-88-48-40-52</span>
            </a>
            <a href="mailto:support@ayokaenergie.com" className="flex items-center gap-2 hover:text-[#EEC17E] transition-colors">
              <Mail className="w-3.5 h-3.5" />
              <span>support@ayokaenergie.com</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-2 text-white/70">
            <Clock className="w-3.5 h-3.5" />
            <span>Lun-Ven : 9h-18h30</span>
          </div>
        </div>
      </div>

      <nav className={`fixed left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'top-0 bg-[#01655B] shadow-lg py-3' : 'top-9 bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#" className="flex items-center gap-3">
          <LogoFull />
        </a>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-white text-sm font-medium tracking-wider uppercase hover:text-[#EEC17E] transition-colors duration-300"
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#devis"
            className="btn-secondary ml-4"
            data-testid="nav-devis-btn"
          >
            Demander un devis
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#01655B] border-t border-white/10 animate-fadeIn">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-white text-sm font-medium tracking-wider uppercase py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#devis"
              className="btn-secondary text-center mt-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Demander un devis
            </a>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
    {/* Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1631545806609-35d4d77c1a41?q=80&w=2070&auto=format&fit=crop')`
      }}
    />
    <div className="absolute inset-0 gradient-overlay" />
    
    {/* Watermark Logo */}
    <div className="absolute right-0 bottom-0 opacity-5">
      <img 
        src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png" 
        alt="" 
        className="w-96 h-96"
      />
    </div>

    <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
      <div className="decorative-line mx-auto mb-8 animate-fadeIn" />
      <h1 className="text-5xl md:text-7xl font-light mb-6 animate-fadeInUp tracking-wide text-black">
        Excellence en<br />
        <span className="text-[#EEC17E]">Génie Climatique</span>
      </h1>
      <p className="text-lg md:text-xl font-light max-w-2xl mx-auto mb-10 animate-fadeInUp stagger-2 leading-relaxed text-black">
        Ayoka Énergie vous accompagne dans tous vos projets de climatisation, 
        chauffage et maintenance en Île-de-France
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp stagger-3">
        <a href="#devis" className="btn-secondary" data-testid="hero-devis-btn">
          Demander un devis gratuit
        </a>
        <a href="#services" className="btn-outline" data-testid="hero-services-btn">
          Découvrir nos services
        </a>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
      <ChevronDown className="text-white w-8 h-8" />
    </div>
  </section>
);

// Services Section with Price Estimator
const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: "installation",
      icon: <Thermometer className="w-10 h-10" />,
      title: "Installation",
      subtitle: "Climatisation & Chauffage",
      description: "Installation professionnelle de systèmes de climatisation et de chauffage adaptés à vos besoins.",
      price: "À partir de 3 000€",
      note: "Devis personnalisé",
      features: ["Étude thermique", "Installation certifiée", "Mise en service", "Garantie décennale"]
    },
    {
      id: "maintenance",
      icon: <Wrench className="w-10 h-10" />,
      title: "Maintenance",
      subtitle: "Entretien régulier",
      description: "Contrats de maintenance pour garantir la performance et la longévité de vos équipements.",
      price: "300€ - 1 500€",
      note: "Selon équipement",
      features: ["Contrôle annuel", "Nettoyage complet", "Vérification fluide", "Rapport détaillé"]
    },
    {
      id: "exploitation",
      icon: <Building2 className="w-10 h-10" />,
      title: "Exploitation",
      subtitle: "Gestion complète",
      description: "Prise en charge complète de l'exploitation de vos installations climatiques.",
      price: "Sur devis",
      note: "Étude personnalisée",
      features: ["Supervision 24/7", "Optimisation énergétique", "Interventions rapides", "Reporting mensuel"]
    }
  ];

  return (
    <section id="services" className="section-padding bg-white relative" data-testid="services-section">
      {/* Watermark */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 opacity-[0.02]">
        <img 
          src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png" 
          alt="" 
          className="w-80 h-80 filter invert"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="decorative-line mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl text-[#01655B] mb-4">Nos Services</h2>
          <p className="text-black max-w-2xl mx-auto">
            Des solutions complètes pour tous vos besoins en génie climatique
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`bg-white border-2 p-8 card-hover cursor-pointer transition-all duration-300 ${
                selectedService === service.id 
                  ? 'border-[#01655B] shadow-xl' 
                  : 'border-gray-100 hover:border-[#EEC17E]'
              }`}
              onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              data-testid={`service-card-${service.id}`}
            >
              <div className="text-[#01655B] mb-6">{service.icon}</div>
              <h3 className="text-2xl text-[#01655B] mb-1">{service.title}</h3>
              <p className="text-[#EEC17E] text-sm uppercase tracking-wider mb-4">{service.subtitle}</p>
              <p className="text-black mb-6 text-sm leading-relaxed">{service.description}</p>
              
              <div className="border-t border-gray-100 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-semibold text-[#01655B]">{service.price}</span>
                  <span className="text-xs text-gray-500 uppercase">{service.note}</span>
                </div>
                
                {selectedService === service.id && (
                  <div className="animate-fadeIn mt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Inclus :</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-black">
                          <CheckCircle className="w-4 h-4 text-[#01655B]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <a 
                href="#devis" 
                className="mt-6 w-full btn-primary flex items-center justify-center gap-2"
                data-testid={`service-devis-btn-${service.id}`}
              >
                Demander un devis <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const stats = [
    { value: "10+", label: "Années d'expérience" },
    { value: "500+", label: "Projets réalisés" },
    { value: "100%", label: "Clients satisfaits" },
    { value: "24/7", label: "Support disponible" }
  ];

  const certifications = [
    { icon: <Shield className="w-8 h-8" />, title: "Assurance Décennale", desc: "Protection garantie sur 10 ans" },
    { icon: <Award className="w-8 h-8" />, title: "Attestation Capacité Fluidique", desc: "Manipulation des fluides frigorigènes certifiée" },
    { icon: <Star className="w-8 h-8" />, title: "Garantie sur chaque intervention", desc: "Votre sérénité, notre engagement absolu" }
  ];

  return (
    <section id="about" className="section-padding bg-[#01655B] text-white relative overflow-hidden" data-testid="about-section">
      {/* Watermark */}
      <div className="absolute right-0 top-0 opacity-5">
        <img 
          src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png" 
          alt="" 
          className="w-96 h-96"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="decorative-line mb-6" />
            <h2 className="text-4xl md:text-5xl mb-6">Pourquoi choisir<br />Ayoka Énergie ?</h2>
            <p className="text-white/80 leading-relaxed mb-8">
              Basée au cœur de Paris, sur les Champs-Élysées, Ayoka Énergie s'est imposée comme 
              une référence du génie climatique en Île-de-France. Notre expertise, combinée à 
              notre engagement envers l'excellence, nous permet de répondre aux exigences 
              les plus élevées de notre clientèle.
            </p>
            <p className="text-white/80 leading-relaxed mb-8">
              Que vous soyez un professionnel de l'hôtellerie, un particulier exigeant ou 
              une entreprise, nous mettons notre savoir-faire à votre service pour des 
              installations performantes et durables.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-semibold text-[#EEC17E] mb-1">{stat.value}</div>
                  <div className="text-xs uppercase tracking-wider text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {certifications.map((cert, index) => (
              <div 
                key={index}
                className="glass-dark p-6 flex items-start gap-4 border border-white/10"
                data-testid={`certification-${index}`}
              >
                <div className="text-[#EEC17E]">{cert.icon}</div>
                <div>
                  <h4 className="text-lg mb-1">{cert.title}</h4>
                  <p className="text-white/60 text-sm">{cert.desc}</p>
                </div>
              </div>
            ))}
            
            {/* CTA */}
            <a 
              href="#devis" 
              className="btn-secondary w-full flex items-center justify-center gap-2 mt-8"
              data-testid="about-devis-btn"
            >
              Obtenir un devis gratuit <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// B2B Section
const B2BSection = () => (
  <section className="section-padding bg-white relative overflow-hidden" data-testid="b2b-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="bg-gradient-to-br from-[#01655B] to-[#014d45] p-10 md:p-16 relative overflow-hidden">
        {/* Watermark */}
        <div className="absolute right-0 top-0 opacity-5">
          <img 
            src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png" 
            alt="" 
            className="w-80 h-80"
          />
        </div>
        
        <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-block px-4 py-1 bg-[#EEC17E] text-[#01655B] text-xs uppercase tracking-widest font-semibold mb-6">
              Professionnels
            </div>
            <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Solution dédiée aux<br />
              <span className="text-[#EEC17E]">entreprises</span>
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              Hôtels, résidences, syndics, bureaux : bénéficiez de solutions de paiement 
              professionnelles adaptées à votre trésorerie.
            </p>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-[#EEC17E] flex-shrink-0 mt-0.5" />
                <span><strong className="text-white">Paiement différé jusqu'à 30, 60 ou 90 jours</strong> via Billie</span>
              </li>
              <li className="flex items-start gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-[#EEC17E] flex-shrink-0 mt-0.5" />
                <span>Facture immédiate pour votre comptabilité</span>
              </li>
              <li className="flex items-start gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-[#EEC17E] flex-shrink-0 mt-0.5" />
                <span>Virement bancaire et carte pro acceptés</span>
              </li>
              <li className="flex items-start gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-[#EEC17E] flex-shrink-0 mt-0.5" />
                <span>Contrats de maintenance annuels sur mesure</span>
              </li>
            </ul>
            
            <a href="#devis" className="btn-secondary inline-flex items-center gap-2" data-testid="b2b-devis-btn">
              Demander un devis pro <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8">
              <h4 className="text-[#EEC17E] text-xs uppercase tracking-widest mb-4">Moyens de paiement acceptés</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 p-4 border border-white/10 text-center">
                  <p className="text-white font-medium text-sm">Carte bancaire</p>
                  <p className="text-white/60 text-xs mt-1">Visa, Mastercard</p>
                </div>
                <div className="bg-[#EEC17E]/20 p-4 border border-[#EEC17E]/40 text-center">
                  <p className="text-[#EEC17E] font-medium text-sm">Billie</p>
                  <p className="text-white/60 text-xs mt-1">Paiement B2B différé</p>
                </div>
                <div className="bg-white/10 p-4 border border-white/10 text-center">
                  <p className="text-white font-medium text-sm">Klarna</p>
                  <p className="text-white/60 text-xs mt-1">Paiement en plusieurs fois</p>
                </div>
                <div className="bg-white/10 p-4 border border-white/10 text-center">
                  <p className="text-white font-medium text-sm">Virement</p>
                  <p className="text-white/60 text-xs mt-1">SEPA</p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-white/60 text-xs">Paiements sécurisés par</p>
                <p className="text-[#EEC17E] font-semibold mt-1">Mollie</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Réalisations Gallery
const RealisationsSection = () => {
  const projects = [
    {
      image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2053&auto=format&fit=crop",
      title: "Hôtel de Luxe Paris 8ème",
      category: "Climatisation"
    },
    {
      image: "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?q=80&w=2070&auto=format&fit=crop",
      title: "Résidence Privée Neuilly",
      category: "Chauffage"
    },
    {
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop",
      title: "Villa Contemporaine Versailles",
      category: "Installation complète"
    },
    {
      image: "https://images.unsplash.com/photo-1600607687644-aac4c3eac7f4?q=80&w=2070&auto=format&fit=crop",
      title: "Bureaux Corporate La Défense",
      category: "Climatisation"
    }
  ];

  return (
    <section id="realisations" className="section-padding bg-gray-50" data-testid="realisations-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="decorative-line mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl text-[#01655B] mb-4">Nos Réalisations</h2>
          <p className="text-black max-w-2xl mx-auto">
            Découvrez quelques-uns de nos projets récents en Île-de-France
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden aspect-[4/3] card-hover"
              data-testid={`project-${index}`}
            >
              <img 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="text-[#EEC17E] text-xs uppercase tracking-wider">{project.category}</span>
                <h3 className="text-white text-xl mt-1">{project.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Request Form
const QuoteSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    description: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/quotes`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        description: '',
        address: ''
      });
    } catch (err) {
      setError("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="section-padding bg-white relative" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <div className="decorative-line mb-6" />
            <h2 className="text-4xl md:text-5xl text-[#01655B] mb-6">Demandez votre<br />devis gratuit</h2>
            <p className="text-black mb-8 leading-relaxed">
              Remplissez ce formulaire et recevez une estimation personnalisée sous 24h. 
              Notre équipe d'experts analysera votre projet pour vous proposer la solution 
              la plus adaptée à vos besoins.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#01655B] flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <span className="text-black">Devis gratuit et sans engagement</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#01655B] flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <span className="text-black">Réponse sous 24 heures</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#01655B] flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <span className="text-black">Garantie décennale incluse</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-8 lg:p-10">
            {success ? (
              <div className="text-center py-12 animate-fadeIn" data-testid="quote-success">
                <CheckCircle className="w-16 h-16 text-[#01655B] mx-auto mb-4" />
                <h3 className="text-2xl text-[#01655B] mb-2">Demande envoyée !</h3>
                <p className="text-black">Nous vous contacterons sous 24 heures.</p>
                <button 
                  onClick={() => setSuccess(false)}
                  className="btn-primary mt-6"
                >
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Nom complet *"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full"
                    data-testid="quote-name"
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full"
                    data-testid="quote-email"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    placeholder="Téléphone *"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full"
                    data-testid="quote-phone"
                  />
                  <select
                    required
                    value={formData.service_type}
                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                    className="w-full"
                    data-testid="quote-service"
                  >
                    <option value="">Type de service *</option>
                    <option value="installation">Installation (à partir de 3000€)</option>
                    <option value="maintenance">Maintenance (300€ - 1500€)</option>
                    <option value="exploitation">Exploitation (sur devis)</option>
                  </select>
                </div>
                <input
                  type="text"
                  placeholder="Adresse du projet *"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full"
                  data-testid="quote-address"
                />
                <textarea
                  placeholder="Décrivez votre projet..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full resize-none"
                  data-testid="quote-description"
                />
                
                {error && (
                  <p className="text-red-500 text-sm" data-testid="quote-error">{error}</p>
                )}

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="quote-submit"
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Intervention Request Form
const InterventionSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    problem_description: '',
    address: '',
    preferred_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/interventions`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        urgency: '',
        problem_description: '',
        address: '',
        preferred_date: ''
      });
    } catch (err) {
      setError("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="intervention" className="section-padding bg-[#01655B]" data-testid="intervention-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="decorative-line mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl text-white mb-4">Besoin d'une intervention ?</h2>
          <p className="text-white/80">
            Panne, dysfonctionnement ? Notre équipe intervient rapidement en Île-de-France
          </p>
        </div>

        <div className="bg-white p-8 lg:p-10">
          {success ? (
            <div className="text-center py-12 animate-fadeIn" data-testid="intervention-success">
              <CheckCircle className="w-16 h-16 text-[#01655B] mx-auto mb-4" />
              <h3 className="text-2xl text-[#01655B] mb-2">Demande enregistrée !</h3>
              <p className="text-black">Nous vous contacterons très rapidement.</p>
              <button 
                onClick={() => setSuccess(false)}
                className="btn-primary mt-6"
              >
                Nouvelle demande
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="intervention-form">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Nom complet *"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full"
                  data-testid="intervention-name"
                />
                <input
                  type="tel"
                  placeholder="Téléphone *"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full"
                  data-testid="intervention-phone"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full"
                  data-testid="intervention-email"
                />
                <select
                  required
                  value={formData.urgency}
                  onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                  className="w-full"
                  data-testid="intervention-urgency"
                >
                  <option value="">Niveau d'urgence *</option>
                  <option value="urgent">Urgent (sous 24h)</option>
                  <option value="normal">Normal (sous 48h)</option>
                  <option value="planifie">Planifié (date souhaitée)</option>
                </select>
              </div>
              <input
                type="text"
                placeholder="Adresse d'intervention *"
                required
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full"
                data-testid="intervention-address"
              />
              {formData.urgency === 'planifie' && (
                <input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                  className="w-full"
                  data-testid="intervention-date"
                />
              )}
              <textarea
                placeholder="Décrivez le problème rencontré *"
                rows={4}
                required
                value={formData.problem_description}
                onChange={(e) => setFormData({...formData, problem_description: e.target.value})}
                className="w-full resize-none"
                data-testid="intervention-description"
              />
              
              {error && (
                <p className="text-red-500 text-sm" data-testid="intervention-error">{error}</p>
              )}

              <button 
                type="submit" 
                className="btn-secondary w-full"
                disabled={loading}
                data-testid="intervention-submit"
              >
                {loading ? 'Envoi en cours...' : 'Demander une intervention'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => (
  <section id="contact" className="section-padding bg-gray-50" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <div className="decorative-line mx-auto mb-6" />
        <h2 className="text-4xl md:text-5xl text-[#01655B] mb-4">Contactez-nous</h2>
        <p className="text-black">Nous sommes à votre disposition pour toute question</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white p-8 text-center card-hover" data-testid="contact-address">
          <div className="w-16 h-16 bg-[#01655B] mx-auto mb-6 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-xl text-[#01655B] mb-2">Adresse</h4>
          <p className="text-black">78 avenue des Champs-Élysées<br />75008 Paris</p>
        </div>

        <div className="bg-white p-8 text-center card-hover" data-testid="contact-phone">
          <div className="w-16 h-16 bg-[#01655B] mx-auto mb-6 flex items-center justify-center">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-xl text-[#01655B] mb-2">Téléphone</h4>
          <p className="text-black">07-88-48-40-52<br />Lun-Ven : 9h-18h30</p>
        </div>

        <div className="bg-white p-8 text-center card-hover" data-testid="contact-email">
          <div className="w-16 h-16 bg-[#01655B] mx-auto mb-6 flex items-center justify-center">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-xl text-[#01655B] mb-2">Email</h4>
          <p className="text-black">support@ayokaenergie.com<br />Réponse sous 24h</p>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-[#01655B] text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-10 mb-12">
        <div className="md:col-span-2">
          <LogoFull />
          <p className="text-white/70 mt-6 max-w-md leading-relaxed">
            Expert en génie climatique depuis plus de 10 ans, Ayoka Énergie 
            accompagne les professionnels et particuliers exigeants en Île-de-France.
          </p>
          <div className="flex gap-6 mt-6">
            <Shield className="w-8 h-8 text-[#EEC17E]" />
            <Award className="w-8 h-8 text-[#EEC17E]" />
          </div>
        </div>

        <div>
          <h5 className="text-lg mb-4">Services</h5>
          <ul className="space-y-2 text-white/70">
            <li><a href="#services" className="hover:text-[#EEC17E] transition-colors">Installation</a></li>
            <li><a href="#services" className="hover:text-[#EEC17E] transition-colors">Maintenance</a></li>
            <li><a href="#services" className="hover:text-[#EEC17E] transition-colors">Exploitation</a></li>
            <li><a href="#intervention" className="hover:text-[#EEC17E] transition-colors">Intervention</a></li>
            <li><a href="/partenaire" className="hover:text-[#EEC17E] transition-colors text-[#EEC17E]">Programme Partenaire</a></li>
          </ul>
        </div>

        <div>
          <h5 className="text-lg mb-4">Contact</h5>
          <ul className="space-y-2 text-white/70">
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              78 av. des Champs-Élysées
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              07-88-48-40-52
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              support@ayokaenergie.com
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/50 text-sm">
          © 2024 Ayoka Énergie. Tous droits réservés.
        </p>
        <div className="flex gap-6 text-white/50 text-sm">
          <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
          <a href="#" className="hover:text-white transition-colors">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  </footer>
);

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Bonjour ! Je suis l'assistant virtuel d'Ayoka Énergie. Comment puis-je vous aider aujourd'hui ?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, je rencontre un problème technique. Veuillez réessayer ou nous contacter directement." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    "Quels sont vos tarifs ?",
    "Zones d'intervention ?",
    "Demander un devis"
  ];

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
          isOpen ? 'bg-[#01655B]' : 'bg-[#EEC17E] hover:scale-110'
        }`}
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <MessageCircle className="w-7 h-7 text-[#01655B]" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white shadow-2xl animate-slideInRight"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-[#01655B] p-4 flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_passage-app-1/artifacts/xslig9f7_Icone_seule_blanche.png"
              alt="Ayoka"
              className="w-10 h-10"
            />
            <div>
              <h4 className="text-white font-medium">Assistant Ayoka</h4>
              <p className="text-white/70 text-xs">En ligne • Répond en quelques secondes</p>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 text-sm ${
                    msg.role === 'user'
                      ? 'bg-[#01655B] text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 text-sm text-gray-500">
                  <span className="animate-pulse">En train d'écrire...</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setInput(action);
                  }}
                  className="text-xs px-3 py-1 bg-gray-100 text-gray-600 hover:bg-[#EEC17E] hover:text-[#01655B] transition-colors"
                  data-testid={`quick-action-${index}`}
                >
                  {action}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écrivez votre message..."
              className="flex-1 text-sm"
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 bg-[#01655B] flex items-center justify-center hover:bg-[#017d70] transition-colors disabled:opacity-50"
              data-testid="chatbot-send"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

// Partner/Parrainage Page
const PartnerPage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    code_parrainage: ''
  });
  
  const [logements, setLogements] = useState([{
    address: '',
    type_logement: '',
    equipements: []
  }]);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [totalHT, setTotalHT] = useState(null);

  const equipementOptions = ['Climatisation', 'VMC', 'Chauffage', 'Électricité', 'Plomberie'];
  const typeLogementOptions = ['Studio', 'T2', 'T3', 'T4+'];

  // Calculate price
  const calculatePrice = () => {
    const nbLogements = logements.length;
    const codeUpper = formData.code_parrainage.toUpperCase();
    const pricePerLogement = codeUpper === 'ESTHER2026' ? 500 : 600;
    return pricePerLogement * nbLogements;
  };

  const addLogement = () => {
    setLogements([...logements, { address: '', type_logement: '', equipements: [] }]);
  };

  const removeLogement = (index) => {
    if (logements.length > 1) {
      setLogements(logements.filter((_, i) => i !== index));
    }
  };

  const updateLogement = (index, field, value) => {
    const updated = [...logements];
    updated[index][field] = value;
    setLogements(updated);
  };

  const toggleEquipement = (index, equipement) => {
    const updated = [...logements];
    const current = updated[index].equipements;
    if (current.includes(equipement)) {
      updated[index].equipements = current.filter(e => e !== equipement);
    } else {
      updated[index].equipements = [...current, equipement];
    }
    setLogements(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate all logements have required fields
    for (let i = 0; i < logements.length; i++) {
      if (!logements[i].address || !logements[i].type_logement) {
        setError(`Veuillez remplir tous les champs du logement ${i + 1}`);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/partners`, {
        ...formData,
        logements
      });
      setTotalHT(response.data.total_ht);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ nom: '', prenom: '', email: '', phone: '', code_parrainage: '' });
    setLogements([{ address: '', type_logement: '', equipements: [] }]);
    setSuccess(false);
    setTotalHT(null);
  };

  const currentPrice = calculatePrice();
  const codeIsValid = formData.code_parrainage.toUpperCase() === 'ESTHER2026';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#01655B] py-6">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <LogoFull />
          </a>
          <a href="/" className="text-white text-sm font-medium tracking-wider uppercase hover:text-[#EEC17E] transition-colors">
            Retour au site
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#01655B] py-12 text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Users className="w-16 h-16 mx-auto mb-6 text-[#EEC17E]" />
          <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Programme Partenaire
          </h1>
          <p className="text-white/80 text-lg">
            Bénéficiez de tarifs préférentiels avec votre code parrainage
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white p-8 lg:p-10 shadow-lg">
            {success ? (
              <div className="text-center py-12 animate-fadeIn" data-testid="partner-success">
                <CheckCircle className="w-16 h-16 text-[#01655B] mx-auto mb-4" />
                <h3 className="text-2xl text-[#01655B] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Demande envoyée !
                </h3>
                <p className="text-black mb-4">
                  Merci pour votre demande. Vous recevrez un email de confirmation.
                </p>
                <div className="bg-[#01655B] text-white p-4 rounded mb-6">
                  <p className="text-2xl font-bold">{totalHT} € HT</p>
                  <p className="text-sm opacity-80">{logements.length} logement(s)</p>
                </div>
                <button onClick={resetForm} className="btn-primary">
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-form">
                {/* Personal Info Section */}
                <div>
                  <h2 className="text-2xl text-[#01655B] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Vos informations
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-black mb-1">Nom *</label>
                      <input
                        type="text"
                        placeholder="Votre nom"
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({...formData, nom: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-black mb-1">Prénom *</label>
                      <input
                        type="text"
                        placeholder="Votre prénom"
                        required
                        value={formData.prenom}
                        onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-black mb-1">Email *</label>
                      <input
                        type="email"
                        placeholder="votre@email.com"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-black mb-1">Téléphone *</label>
                      <input
                        type="tel"
                        placeholder="06 XX XX XX XX"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">Code Parrainage *</label>
                    <input
                      type="text"
                      placeholder="Entrez votre code parrainage"
                      required
                      value={formData.code_parrainage}
                      onChange={(e) => setFormData({...formData, code_parrainage: e.target.value})}
                      className={`w-full ${codeIsValid ? 'border-green-500' : ''}`}
                    />
                    {formData.code_parrainage && (
                      <p className={`text-sm mt-1 ${codeIsValid ? 'text-green-600' : 'text-gray-500'}`}>
                        {codeIsValid ? '✓ Code valide - Tarif préférentiel : 500 € HT / logement' : 'Tarif standard : 600 € HT / logement'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Logements Section */}
                <div className="border-t pt-6">
                  <h2 className="text-2xl text-[#01655B] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Logement(s) concerné(s)
                  </h2>
                  
                  {logements.map((logement, index) => (
                    <div key={index} className="bg-gray-50 p-6 mb-4 relative border border-gray-200">
                      {logements.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLogement(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                      
                      <h4 className="font-medium text-[#01655B] mb-4">Logement {index + 1}</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Adresse complète *</label>
                          <input
                            type="text"
                            placeholder="Adresse du logement"
                            required
                            value={logement.address}
                            onChange={(e) => updateLogement(index, 'address', e.target.value)}
                            className="w-full"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Type de logement *</label>
                          <select
                            required
                            value={logement.type_logement}
                            onChange={(e) => updateLogement(index, 'type_logement', e.target.value)}
                            className="w-full"
                          >
                            <option value="">Sélectionnez un type</option>
                            {typeLogementOptions.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-2">Équipements présents</label>
                          <div className="flex flex-wrap gap-2">
                            {equipementOptions.map(equip => (
                              <label
                                key={equip}
                                className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                                  logement.equipements.includes(equip)
                                    ? 'bg-[#01655B] text-white border-[#01655B]'
                                    : 'bg-white text-black border-gray-300 hover:border-[#01655B]'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={logement.equipements.includes(equip)}
                                  onChange={() => toggleEquipement(index, equip)}
                                  className="sr-only"
                                />
                                <span className="text-sm">{equip}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addLogement}
                    className="w-full py-3 border-2 border-dashed border-[#01655B] text-[#01655B] hover:bg-[#01655B] hover:text-white transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">+</span> Ajouter un logement
                  </button>
                </div>

                {/* Price Summary */}
                <div className="bg-[#01655B] text-white p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Total estimé</p>
                      <p className="text-3xl font-bold">{currentPrice} € HT</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">{logements.length} logement(s)</p>
                      <p className="text-sm opacity-80">× {codeIsValid ? '500' : '600'} € HT</p>
                    </div>
                  </div>
                  {!codeIsValid && formData.code_parrainage && (
                    <p className="text-[#EEC17E] text-sm mt-2">
                      💡 Avec un code parrainage valide : {500 * logements.length} € HT
                    </p>
                  )}
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <button 
                  type="submit" 
                  className="btn-secondary w-full text-lg py-4"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Valider ma demande'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#01655B] py-8 text-center text-white/70">
        <p>© 2024 Ayoka Énergie. Tous droits réservés.</p>
        <p className="mt-2">
          <a href="/" className="hover:text-[#EEC17E] transition-colors">Retour au site principal</a>
        </p>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      // Poll for payment status
      const checkStatus = async () => {
        try {
          const res = await axios.get(`${API}/payments/${ref}`);
          setPayment(res.data);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      checkStatus();
      // Poll every 3 seconds for status update
      const interval = setInterval(checkStatus, 3000);
      setTimeout(() => clearInterval(interval), 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, []);

  const isPaid = payment?.status === 'paid';
  const isPending = payment?.status === 'pending' || payment?.status === 'open';
  const isFailed = payment?.status === 'failed' || payment?.status === 'canceled' || payment?.status === 'expired';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#01655B] py-6">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <LogoFull />
          </a>
          <a href="/" className="text-white text-sm font-medium tracking-wider uppercase hover:text-[#EEC17E] transition-colors">
            Retour au site
          </a>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center py-16 px-6">
        <div className="max-w-md w-full bg-white p-10 shadow-lg text-center">
          {loading ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#01655B] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-black">Vérification du paiement...</p>
            </>
          ) : isPaid ? (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-3xl text-[#01655B] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Paiement confirmé !
              </h1>
              <p className="text-black mb-4">Merci pour votre paiement.</p>
              <div className="bg-gray-50 p-4 mb-4">
                <p className="text-2xl font-bold text-[#01655B]">{payment.amount?.toFixed(2)} €</p>
                <p className="text-sm text-gray-600">{payment.description}</p>
              </div>
              <p className="text-sm text-black">Un reçu vous a été envoyé par email.</p>
            </>
          ) : isPending ? (
            <>
              <Clock className="w-16 h-16 text-[#EEC17E] mx-auto mb-4" />
              <h1 className="text-3xl text-[#01655B] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Paiement en cours
              </h1>
              <p className="text-black mb-4">Votre paiement est en cours de traitement.</p>
              <p className="text-sm text-gray-600">Cette page se mettra à jour automatiquement.</p>
            </>
          ) : isFailed ? (
            <>
              <X className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-3xl text-red-600 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Paiement échoué
              </h1>
              <p className="text-black mb-4">Le paiement n'a pas pu être finalisé.</p>
              <p className="text-sm text-black">Veuillez nous contacter au 07-88-48-40-52 pour toute assistance.</p>
            </>
          ) : (
            <>
              <p className="text-black">Informations non disponibles.</p>
              <a href="/" className="btn-primary inline-block mt-4">Retour à l'accueil</a>
            </>
          )}
          
          <a href="/" className="btn-primary inline-block mt-6">
            Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('payment');
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_street: '',
    customer_postal_code: '',
    customer_city: '',
    customer_country: 'FR',
    amount: '',
    description: '',
    quote_id: '',
    is_business: false,
    company_name: '',
    vat_number: '',
    siret: '',
    company_registration_number: '',
    product_category: ''
  });
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // Data lists
  const [quotes, setQuotes] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [payments, setPayments] = useState([]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      localStorage.setItem('admin_password', password);
      loadData(password);
    } catch (err) {
      setLoginError('Mot de passe incorrect');
    }
  };
  
  useEffect(() => {
    const saved = localStorage.getItem('admin_password');
    if (saved) {
      setPassword(saved);
      axios.post(`${API}/admin/login`, { password: saved })
        .then(() => {
          setIsLoggedIn(true);
          loadData(saved);
        })
        .catch(() => localStorage.removeItem('admin_password'));
    }
  }, []);
  
  const loadData = async (pwd) => {
    const config = { headers: { 'X-Admin-Password': pwd } };
    try {
      const [q, i, p, py] = await Promise.all([
        axios.get(`${API}/admin/quotes`, config),
        axios.get(`${API}/admin/interventions`, config),
        axios.get(`${API}/admin/partners`, config),
        axios.get(`${API}/admin/payments`, config)
      ]);
      setQuotes(q.data);
      setInterventions(i.data);
      setPartners(p.data);
      setPayments(py.data);
    } catch (err) {
      console.error(err);
    }
  };
  
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    setPaymentError('');
    setPaymentResult(null);
    
    try {
      const response = await axios.post(
        `${API}/payments/create-link`,
        {
          ...paymentForm,
          amount: parseFloat(paymentForm.amount)
        },
        { headers: { 'X-Admin-Password': password } }
      );
      setPaymentResult(response.data);
      setPaymentForm({ customer_name: '', customer_email: '', customer_phone: '', customer_street: '', customer_postal_code: '', customer_city: '', customer_country: 'FR', amount: '', description: '', quote_id: '', is_business: false, company_name: '', vat_number: '', siret: '', company_registration_number: '', product_category: '' });
      loadData(password);
    } catch (err) {
      setPaymentError(err.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };
  
  const logout = () => {
    setIsLoggedIn(false);
    setPassword('');
    localStorage.removeItem('admin_password');
  };
  
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6">
        <div className="bg-white p-10 shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <LogoIcon />
            <h1 className="text-3xl text-[#01655B] mt-4 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Espace Administrateur
            </h1>
            <p className="text-black text-sm">Connexion requise</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-black mb-1">Mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                placeholder="Mot de passe administrateur"
              />
            </div>
            {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
            <button type="submit" className="btn-primary w-full">
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('fr-FR');
  };
  
  const getStatusBadge = (status) => {
    const colors = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      open: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      canceled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#01655B] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoFull />
            <span className="text-white font-medium ml-2">/ Admin</span>
          </div>
          <button onClick={logout} className="text-white hover:text-[#EEC17E] text-sm uppercase tracking-wider">
            Déconnexion
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {[
            { id: 'payment', label: 'Créer un paiement', icon: <Send className="w-4 h-4" /> },
            { id: 'payments', label: `Paiements (${payments.length})`, icon: <CheckCircle className="w-4 h-4" /> },
            { id: 'quotes', label: `Devis (${quotes.length})`, icon: <Mail className="w-4 h-4" /> },
            { id: 'interventions', label: `Interventions (${interventions.length})`, icon: <Wrench className="w-4 h-4" /> },
            { id: 'partners', label: `Partenaires (${partners.length})`, icon: <Users className="w-4 h-4" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-[#01655B] text-[#01655B]' 
                  : 'border-transparent text-gray-600 hover:text-[#01655B]'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'payment' && (
          <div className="bg-white p-8 shadow max-w-2xl">
            <h2 className="text-2xl text-[#01655B] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Créer un lien de paiement
            </h2>
            
            {paymentResult && (
              <div className="bg-green-50 border border-green-200 p-4 mb-6">
                <p className="text-green-800 font-medium">✓ Lien de paiement créé et envoyé par email</p>
                <p className="text-sm text-green-700 mt-2 break-all">
                  Lien : <a href={paymentResult.checkout_url} target="_blank" rel="noopener noreferrer" className="underline">{paymentResult.checkout_url}</a>
                </p>
              </div>
            )}
            
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-1">Nom du client *</label>
                  <input
                    type="text"
                    required
                    value={paymentForm.customer_name}
                    onChange={(e) => setPaymentForm({...paymentForm, customer_name: e.target.value})}
                    className="w-full"
                    placeholder="Prénom Nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">Email du client *</label>
                  <input
                    type="email"
                    required
                    value={paymentForm.customer_email}
                    onChange={(e) => setPaymentForm({...paymentForm, customer_email: e.target.value})}
                    className="w-full"
                    placeholder="client@entreprise.com"
                  />
                  {(paymentForm.customer_email.toLowerCase().includes('ayokaenergie.com') || 
                    paymentForm.customer_email.toLowerCase() === 'support@ayokaenergie.com') && (
                    <p className="text-red-600 text-xs mt-1 font-medium">
                      ⚠️ Attention : n'utilisez pas votre propre email. Billie bloquerait le paiement (auto-facturation).
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Téléphone du client</label>
                <input
                  type="tel"
                  value={paymentForm.customer_phone}
                  onChange={(e) => setPaymentForm({...paymentForm, customer_phone: e.target.value})}
                  className="w-full"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-4 space-y-3">
                <p className="text-sm font-medium text-black">Adresse du client (obligatoire pour Klarna/Billie)</p>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Rue et numéro *</label>
                  <input
                    type="text"
                    required
                    value={paymentForm.customer_street}
                    onChange={(e) => setPaymentForm({...paymentForm, customer_street: e.target.value})}
                    className="w-full"
                    placeholder="15 rue de la Paix"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Code postal *</label>
                    <input
                      type="text"
                      required
                      value={paymentForm.customer_postal_code}
                      onChange={(e) => setPaymentForm({...paymentForm, customer_postal_code: e.target.value})}
                      className="w-full"
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Ville *</label>
                    <input
                      type="text"
                      required
                      value={paymentForm.customer_city}
                      onChange={(e) => setPaymentForm({...paymentForm, customer_city: e.target.value})}
                      className="w-full"
                      placeholder="Paris"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Pays *</label>
                    <select
                      required
                      value={paymentForm.customer_country}
                      onChange={(e) => setPaymentForm({...paymentForm, customer_country: e.target.value})}
                      className="w-full"
                    >
                      <option value="FR">France</option>
                      <option value="BE">Belgique</option>
                      <option value="DE">Allemagne</option>
                      <option value="NL">Pays-Bas</option>
                      <option value="AT">Autriche</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Montant en € *</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  required
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  className="w-full"
                  placeholder="500.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Description / Objet *</label>
                <textarea
                  required
                  rows={3}
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                  className="w-full resize-none"
                  placeholder="Ex: Devis N°123 - Installation climatisation appartement Paris 16"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Référence devis (optionnel)</label>
                <input
                  type="text"
                  value={paymentForm.quote_id}
                  onChange={(e) => setPaymentForm({...paymentForm, quote_id: e.target.value})}
                  className="w-full"
                  placeholder="ID du devis (optionnel)"
                />
              </div>
              
              <div className="bg-gray-50 p-4 border border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentForm.is_business}
                    onChange={(e) => setPaymentForm({...paymentForm, is_business: e.target.checked})}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-black">Client professionnel (B2B) - Activer Billie</div>
                    <div className="text-xs text-gray-600 mt-1">
                      <strong>Obligatoire pour Billie :</strong> Cochez si le client est une entreprise. Vous devrez indiquer la raison sociale ci-dessous.
                    </div>
                  </div>
                </label>
                
                {paymentForm.is_business && (
                  <div className="mt-4 space-y-3 pl-7">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Raison sociale (nom entreprise) *</label>
                      <input
                        type="text"
                        required={paymentForm.is_business}
                        value={paymentForm.company_name}
                        onChange={(e) => setPaymentForm({...paymentForm, company_name: e.target.value})}
                        className="w-full"
                        placeholder="Ex: HOTEL LUTETIA SAS"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">SIRET</label>
                        <input
                          type="text"
                          value={paymentForm.siret}
                          onChange={(e) => setPaymentForm({...paymentForm, siret: e.target.value})}
                          className="w-full"
                          placeholder="123 456 789 00012"
                        />
                        <p className="text-xs text-gray-500 mt-1">Aide Billie à localiser l'entreprise</p>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Numéro TVA</label>
                        <input
                          type="text"
                          value={paymentForm.vat_number}
                          onChange={(e) => setPaymentForm({...paymentForm, vat_number: e.target.value})}
                          className="w-full"
                          placeholder="FR12345678901"
                        />
                        <p className="text-xs text-gray-500 mt-1">Vérification EU registries</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Catégorie de prestation</label>
                      <select
                        value={paymentForm.product_category}
                        onChange={(e) => setPaymentForm({...paymentForm, product_category: e.target.value})}
                        className="w-full"
                      >
                        <option value="">Sélectionnez une catégorie</option>
                        <option value="Installation climatisation">Installation climatisation</option>
                        <option value="Installation chauffage">Installation chauffage</option>
                        <option value="Maintenance HVAC">Maintenance HVAC</option>
                        <option value="Contrat exploitation">Contrat exploitation</option>
                        <option value="Dépannage urgent">Dépannage urgent</option>
                        <option value="Prestation service">Prestation service</option>
                      </select>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 p-3">
                      <p className="text-xs text-yellow-800">
                        💡 <strong>Astuce Billie :</strong> Plus les informations entreprise sont complètes (SIRET, TVA, adresse exacte), plus Billie pourra valider rapidement le client.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {paymentError && <p className="text-red-500 text-sm">{paymentError}</p>}
              
              <button 
                type="submit" 
                disabled={paymentLoading}
                className="btn-primary w-full"
              >
                {paymentLoading ? 'Création en cours...' : 'Créer et envoyer le lien'}
              </button>
            </form>
          </div>
        )}
        
        {activeTab === 'payments' && (
          <div className="bg-white shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#01655B] text-white">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Montant</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucun paiement</td></tr>
                ) : payments.map(p => (
                  <tr key={p.payment_id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{formatDate(p.created_at)}</td>
                    <td className="p-3">
                      <div>{p.customer_name}</div>
                      <div className="text-xs text-gray-500">{p.customer_email}</div>
                    </td>
                    <td className="p-3 font-medium">{p.amount?.toFixed(2)} €</td>
                    <td className="p-3">{p.description}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs uppercase ${getStatusBadge(p.status)}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'quotes' && (
          <div className="bg-white shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#01655B] text-white">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Service</th>
                  <th className="p-3 text-left">Adresse</th>
                  <th className="p-3 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucun devis</td></tr>
                ) : quotes.map(q => (
                  <tr key={q.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{formatDate(q.created_at)}</td>
                    <td className="p-3">
                      <div>{q.name}</div>
                      <div className="text-xs text-gray-500">{q.email}</div>
                      <div className="text-xs text-gray-500">{q.phone}</div>
                    </td>
                    <td className="p-3">{q.service_type}</td>
                    <td className="p-3">{q.address}</td>
                    <td className="p-3 max-w-xs truncate">{q.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'interventions' && (
          <div className="bg-white shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#01655B] text-white">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Urgence</th>
                  <th className="p-3 text-left">Adresse</th>
                  <th className="p-3 text-left">Problème</th>
                </tr>
              </thead>
              <tbody>
                {interventions.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucune intervention</td></tr>
                ) : interventions.map(i => (
                  <tr key={i.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{formatDate(i.created_at)}</td>
                    <td className="p-3">
                      <div>{i.name}</div>
                      <div className="text-xs text-gray-500">{i.email}</div>
                      <div className="text-xs text-gray-500">{i.phone}</div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs uppercase ${
                        i.urgency === 'urgent' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {i.urgency}
                      </span>
                    </td>
                    <td className="p-3">{i.address}</td>
                    <td className="p-3 max-w-xs truncate">{i.problem_description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'partners' && (
          <div className="bg-white shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#01655B] text-white">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Contact</th>
                  <th className="p-3 text-left">Code</th>
                  <th className="p-3 text-left">Logements</th>
                  <th className="p-3 text-left">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucun partenaire</td></tr>
                ) : partners.map(p => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{formatDate(p.created_at)}</td>
                    <td className="p-3">
                      <div>{p.prenom} {p.nom}</div>
                      <div className="text-xs text-gray-500">{p.email}</div>
                      <div className="text-xs text-gray-500">{p.phone}</div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        p.code_parrainage === 'ESTHER2026' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {p.code_parrainage}
                      </span>
                    </td>
                    <td className="p-3">{p.logements?.length || 0} logement(s)</td>
                    <td className="p-3 font-medium">{p.total_ht} € HT</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <B2BSection />
      <RealisationsSection />
      <QuoteSection />
      <InterventionSection />
      <ContactSection />
      <Footer />
      <Chatbot />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/partenaire" element={<PartnerPage />} />
          <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
